import ItemRenderer from "../ItemRenderer";
import SVGColorFilter from "../SVGColorFilter";
import SVGWrapper from "../SVGWrapper";

interface CharacterPreviewProps {
	template: CharacterTemplate;
	skinTone: ColorTypes;
	hair?: Style;
	eyeball?: ColorTypes;
	iris: ColorTypes;
	top?: Style;
	bottom?: Style;
	accessories?: Style[];
	size?: "sm" | "md" | "lg" | "xl" | "xxl" | "xs";
	className?: string;
	castShadow?: boolean;
}

const CharacterPreview: React.FC<CharacterPreviewProps> = ({
	template,
	skinTone,
	hair,
	eyeball = { id: "eyeball1", color: "#ffffff", name: "white" },
	iris,
	top,
	bottom,
	accessories,
	size = "md",
	className = "",
	castShadow = false,
}) => {
	// Size mapping for different preview sizes
	const sizeClasses = {
		xs: "w-8 h-8 md:w-16 h-16",
		sm: "w-16 h-16 md:w-24 h-24",
		md: "w-24 h-24 md:w-32 h-32",
		lg: "w-32 h-32 md:w-48 h-48",
		xl: "w-48 h-48 md:w-64 h-64",
		xxl: "w-64 h-64 md:w-96 h-96",
	};

	const lipcheeks = { id: "lipcheeks1", color: "#ee7c82", name: "lightpink" };

	// Generate a unique ID for the filter to avoid conflicts with multiple instances
	// const filterId = (prefix: string) =>
	// 	`${prefix}-${Math.random().toString(36).substring(2, 9)}`;
	const randomId = Math.random().toString(36).substring(2, 9);
	const filterId = (prefix: string) => `${prefix}-${randomId}`;
	const viewBox = `0 0 ${template.width} ${template.height}`;

	return (
		<div
			className={`character-preview relative ${sizeClasses[size]} ${className}`}
		>
			<SVGWrapper viewBox={viewBox} className="w-full h-full">
				<defs>
					{castShadow && (
						<filter id="drop-shadow">
							<feOffset in="SourceAlpha" dx="0.2" dy="0.4" />
							<feGaussianBlur stdDeviation="2" />
							<feBlend in="SourceGraphic" in2="blurOut" />
						</filter>
					)}
					{[
						["skin", skinTone?.color],
						["hair", hair?.color],
						["eyeball", eyeball?.color],
						["iris", iris?.color],
						["top", top?.color],
						["bottom", bottom?.color],
						["lipcheeks", lipcheeks?.color],
					].map(([prefix, color]) => (
						<SVGColorFilter
							key={prefix}
							id={filterId(prefix!)}
							color={color!}
						/>
					))}
					{accessories?.map((accessory, index) => {
						const filterIdx = filterId(`accessory-${index}`);
						return (
							<SVGColorFilter
								key={filterIdx}
								id={filterIdx}
								color={accessory.color!}
							/>
						);
					})}
				</defs>
				<ItemRenderer
					item={skinTone!}
					src={template.body}
					filterId={filterId("skin")}
				/>
				<ItemRenderer
					item={eyeball!}
					src={template.eyeball}
					filterId={filterId("eyeball")}
				/>
				<ItemRenderer
					item={iris!}
					src={template.iris}
					filterId={filterId("iris")}
				/>

				<ItemRenderer
					item={lipcheeks}
					src={template.lipcheeks}
					filterId={filterId("lipcheeks")}
					opacity="0.4"
				/>

				{bottom && bottom.style && (
					<ItemRenderer item={bottom} filterId={filterId("bottom")} />
				)}

				{top && top.style && (
					<ItemRenderer item={top!} filterId={filterId("top")} />
				)}

				{hair && hair.style && (
					<ItemRenderer item={hair!} filterId={filterId("hair")} />
				)}

				{accessories &&
					accessories.map((accessory, index) => (
						<ItemRenderer
							key={index}
							item={accessory}
							filterId={filterId(`accessory-${index}`)}
						/>
					))}
			</SVGWrapper>
		</div>
	);
};

export default CharacterPreview;
